import { useEffect, useState } from 'react';
import { useAuth, API } from '../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export default function AdminDashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_users: 0,
    total_transactions: 0,
    completed_transactions: 0,
    pending_reports: 0
  });
  const [reports, setReports] = useState([]);

  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'moderator') {
      navigate('/app');
      toast.error(t('errors.unauthorized'));
    } else {
      fetchAdminData();
    }
  }, [user, navigate]);

  const fetchAdminData = async () => {
    try {
      const [statsRes, reportsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/reports`, { withCredentials: true })
      ]);

      setStats(statsRes.data);
      setReports(reportsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error(t('errors.generic'));
    }
  };

  const handleUpdateReport = async (reportId, status) => {
    try {
      await axios.put(`${API}/admin/reports/${reportId}?status=${status}`, {}, { withCredentials: true });
      toast.success(t('admin.resolve'));
      fetchAdminData();
    } catch (error) {
      console.error('Error updating report:', error);
      toast.error('Failed to update report');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-700 border-yellow-300',
      'reviewed': 'bg-blue-100 text-blue-700 border-blue-300',
      'resolved': 'bg-green-100 text-green-700 border-green-300'
    };
    return colors[status] || colors.pending;
  };

  return (
    <Layout>
      <div data-testid="admin-dashboard" className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">{t('admin.title')}</h1>
          <Badge className="bg-red-100 text-red-600 px-4 py-2 text-base">
            {user?.role}
          </Badge>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="p-6 border-l-4 border-teal-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">{t('admin.totalUsers')}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total_users}</p>
              </div>
              <Users className="h-10 w-10 text-teal-500" />
            </div>
          </Card>

          <Card className="p-6 border-l-4 border-cyan-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">{t('admin.totalTransactions')}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total_transactions}</p>
              </div>
              <Clock className="h-10 w-10 text-cyan-500" />
            </div>
          </Card>

          <Card className="p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">{t('admin.completedTransactions')}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.completed_transactions}</p>
              </div>
              <CheckCircle className="h-10 w-10 text-green-500" />
            </div>
          </Card>

          <Card className="p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">{t('admin.pendingReports')}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.pending_reports}</p>
              </div>
              <AlertTriangle className="h-10 w-10 text-red-500" />
            </div>
          </Card>
        </div>

        {/* Reports */}
        <Card className="p-6">
          <h2 className="text-xl font-bold mb-6">{t('admin.userReports')}</h2>
          <div className="space-y-4">
            {reports.length === 0 ? (
              <p className="text-gray-500 text-center py-8">{t('admin.noReports')}</p>
            ) : (
              reports.map((report) => (
                <div key={report.id} className="p-4 bg-gray-50 rounded-lg border-l-4 border-red-500">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <Badge className={`px-3 py-1 border ${getStatusColor(report.status)}`}>
                        {report.status}
                      </Badge>
                      <p className="font-semibold text-gray-900 mt-2">Reason: {report.reason}</p>
                    </div>
                    <p className="text-sm text-gray-500">
                      {new Date(report.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <p className="text-gray-700 mb-4">{report.description}</p>
                  <div className="flex gap-2">
                    {report.status === 'pending' && (
                      <>
                        <Button
                          data-testid={`review-report-${report.id}`}
                          onClick={() => handleUpdateReport(report.id, 'reviewed')}
                          size="sm"
                          className="bg-blue-500 hover:bg-blue-600 text-white"
                        >
                          Mark Reviewed
                        </Button>
                        <Button
                          data-testid={`resolve-report-${report.id}`}
                          onClick={() => handleUpdateReport(report.id, 'resolved')}
                          size="sm"
                          className="bg-green-500 hover:bg-green-600 text-white"
                        >
                          Resolve
                        </Button>
                      </>
                    )}
                    {report.status === 'reviewed' && (
                      <Button
                        data-testid={`resolve-reviewed-report-${report.id}`}
                        onClick={() => handleUpdateReport(report.id, 'resolved')}
                        size="sm"
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        Resolve
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </Layout>
  );
}